import {Button, Divider, Form, Input, Select, Spin} from 'antd';
import {jwtDecode} from 'jwt-decode';
import {useEffect, useState} from 'react';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';

import {RQGetUserDetails, RQUpdateUserProfileAPI, RqGetDeliveryCharges} from '../../../reactQuery/apis';
import {selectAuth} from '../../../redux/selectors';

const PersonalInformation = () => {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [initialValues, setInitialValues] = useState(null);

  const auth = useSelector(selectAuth);
  const token = auth?.tokenData;

  const decodedToken = token ? jwtDecode(token) : null;
  const id = decodedToken?.userId;

  const {data: userData, isLoading, isError, error} =
    RQGetUserDetails(id, token);

  const {mutate: updateUserProfile} = RQUpdateUserProfileAPI();

  const {data: stateData} = RqGetDeliveryCharges(token);

  useEffect(() => {
    if (userData) {
      const values = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        mobile: userData.mobile,
        address: userData.address,
        street: userData.street,
        apartment: userData.apartment,
        pinCode: userData.pinCode,
        city: userData.city,
        district: userData.district,
        state: userData.state,
      };

      form.setFieldsValue(values);
      setInitialValues(values);
    }
  }, [userData, form]);

  const onFinish = values => {
    const formData = {
      ...values,
      email: userData?.email,
    };

    updateUserProfile(
      {token, formData, id},
      {
        onSuccess: () => {
          toast.success('Profile updated successfully!');
          setIsEditing(false);
          setInitialValues(formData);
        },
        onError: () => {
          toast.error('Failed to update profile');
        },
      },
    );
  };

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    setIsEditing(false);
    form.setFieldsValue(initialValues);
  };

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center'}>
        <Spin />
      </div>
    );
  }

  if (isError) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <div className={'flex flex-col'}>
      <h1 className={'text-xl text-primary-900 font-serif-custom p-2'}>
        Personal Information
      </h1>

      <Divider />

      <div className={'mx-auto w-full p-3 bg-white rounded mt-5'}>

        <Form
          form={form}
          layout={'vertical'}
          onFinish={onFinish}
        >

          {/* Name */}
          <div className={'grid lg:grid-cols-2 gap-3'}>
            <Form.Item
              name={'firstName'}
              label={'First Name'}
              rules={[
                {required: true, message: 'Enter first name'},
                {pattern: /^[A-Za-z]+$/, message: 'Only letters allowed'},
              ]}
            >
              <Input disabled={!isEditing} />
            </Form.Item>

            <Form.Item
              name={'lastName'}
              label={'Last Name'}
              rules={[
                {required: true, message: 'Enter last name'},
                {pattern: /^[A-Za-z]+$/, message: 'Only letters allowed'},
              ]}
            >
              <Input disabled={!isEditing} />
            </Form.Item>
          </div>

          {/* Email + Mobile */}
          <div className={'grid lg:grid-cols-2 gap-3'}>
            <Form.Item
              name={'email'}
              label={'Email'}
              rules={[
                {required: true},
                {type: 'email', message: 'Invalid email'},
              ]}
            >
              <Input disabled />
            </Form.Item>

            <Form.Item
              name={'mobile'}
              label={'Mobile Number'}
              normalize={v => v?.replace(/\D/g, '').slice(0, 10)}
              rules={[
                {required: true, message: 'Enter mobile number'},
                {pattern: /^[0-9]{10}$/, message: 'Must be 10 digits'},
              ]}
            >
              <Input disabled={!isEditing} />
            </Form.Item>
          </div>

          {/* Address */}
          <Form.Item
            name={'address'}
            label={'Address'}
            rules={[
              {required: true, message: 'Enter address'},
            ]}
          >
            <Input.TextArea rows={3} disabled={!isEditing} />
          </Form.Item>

          {/* Street + Apartment */}
          <div className={'grid lg:grid-cols-2 gap-3'}>
            <Form.Item
              name={'street'}
              label={'Street'}
              rules={[{required: true}]}
            >
              <Input disabled={!isEditing} />
            </Form.Item>

            <Form.Item
              name={'apartment'}
              label={'Apartment'}
            >
              <Input disabled={!isEditing} />
            </Form.Item>
          </div>

          {/* PinCode + City */}
          <div className={'grid lg:grid-cols-2 gap-3'}>
            <Form.Item
              name={'pinCode'}
              label={'Pin Code'}
              normalize={v => v?.replace(/\D/g, '').slice(0, 6)}
              rules={[
                {required: true},
                {pattern: /^[0-9]{6}$/, message: 'Invalid pin code'},
              ]}
            >
              <Input disabled={!isEditing} />
            </Form.Item>

            <Form.Item
              name={'city'}
              label={'City'}
              rules={[{required: true}]}
            >
              <Input disabled={!isEditing} />
            </Form.Item>
          </div>

          {/* District + State */}
          <div className={'grid lg:grid-cols-2 gap-3'}>
            <Form.Item
              name={'state'}
              label={'State'}
              rules={[{required: true, message: 'Select state'}]}
            >
              <Select
                placeholder={'Select state'}
                disabled={!isEditing}
                allowClear
              >
                {Array.isArray(stateData) &&
      stateData
        .flatMap(item => item.state)
        .map((state, index) => (
          <Select.Option key={index} value={state}>
            {state}
          </Select.Option>
        ))}
              </Select>
            </Form.Item>

            <Form.Item
              name={'district'}
              label={'District'}
              rules={[{required: true}]}
            >
              <Input disabled={!isEditing} />
            </Form.Item>
          </div>

          {/* Buttons */}

          <Form.Item>

            {isEditing ? (

              <div className={'flex justify-center gap-3'}>

                <Button
                  type={'primary'}
                  htmlType={'submit'}
                  className={'!bg-primary-800'}
                >
        Save
                </Button>

                <Button
                  htmlType={'button'}
                  onClick={handleCancel}
                >
        Cancel
                </Button>

              </div>

            ) : (

              <div className={'flex justify-center'}>

                <Button
                  type={'primary'}
                  htmlType={'button'}
                  className={'!bg-primary-800 w-40'}
                  onClick={e => {
                    e.preventDefault();   // extra safety
                    setIsEditing(true);
                  }}
                >
  Edit
                </Button>
              </div>

            )}

          </Form.Item>

        </Form>

      </div>
    </div>
  );
};

export default PersonalInformation;
